
import React from 'react';

import { Dialog } from '../../base/dialog';
import { MEDIA_TYPE } from '../../base/media';
import { getLocalParticipant, getParticipantDisplayName } from '../../base/participants';
import { forceMuteAllParticipants } from '../actions';

import AbstractMuteRemoteParticipantsVideoDialog, {
    type Props as AbstractProps
} from './AbstractMuteRemoteParticipantsVideoDialog';

/**
 * The type of the React {@code Component} props of
 * {@link AbstractMuteEveryonesVideoDialog}.
 */
export type Props = AbstractProps & {

    content: string,
    exclude: Array<string>,
    title: string,
    _lockAudio: boolean,
    content2: string,
    title1: string
};

/**
 *
 * An abstract Component with the contents for a dialog that asks for confirmation
 * from the user before disabling all remote participants cameras.
 *
 * @augments AbstractMuteRemoteParticipantsVideoDialog
 */
export default class AbstractForceMuteEveryonesVideoDialog<P: Props>
    extends AbstractMuteRemoteParticipantsVideoDialog<P> {
    static defaultProps = {
        exclude: [],
        muteLocal: false
    };

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { content, title } = this.props;

        return (
            <Dialog
                okKey = 'dialog.muteParticipantsVideoButton'
                onSubmit = { this._onSubmit }
                titleString = { title }
                width = 'small'>
                <div>
                    { content }
                </div>
            </Dialog>
        );
    }

    _onSubmit: () => boolean;

    /**
     * Callback to be invoked when the value of this dialog is submitted.
     *
     * @returns {boolean}
     */
    _onSubmit() {
        const {
            dispatch,
            exclude
        } = this.props;

        dispatch(forceMuteAllParticipants(exclude, MEDIA_TYPE.VIDEO));

        return true;
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code AbstractMuteEveryonesVideoDialog}'s props.
 *
 * @param {Object} state - The redux state.
 * @param {Object} ownProps - The properties explicitly passed to the component.
 * @returns {Props}
 */
export function abstractMapStateToProps(state: Object, ownProps: Props) {
    const { exclude = [], t } = ownProps;
    const { lockVideo = {} } = state['features/base/conference'];
    const partipicipants = state['features/base/participants'];
    const remoteLockVideos = Object.values(lockVideo || {}).filter(
        participantId => partipicipants.find(p => p.local)
            && partipicipants.find(p => p.id === participantId)?.role !== 'moderator'
    );

    const fMutedorNot = Object.values(remoteLockVideos).some(value => Boolean(value));


    const whom = exclude

        .map(id => {
            if (id === getLocalParticipant(state).id) {
                return t('dialog.muteEveryoneSelf');
            }

            return getParticipantDisplayName(state, id);
        })
        .join(', ');

    return whom.length ? {
        content: t('dialog.forceMuteEveryonesVideoDialog'),
        title: t('dialog.forceMuteEveryonesVideoTitle', { whom }),
        content2: t('dialog.unforceMuteEveryonesVideoDialog'),
        title1: t('dialog.unforceMuteEveryonesVideoTitle', { whom }),
        _lockAudio: fMutedorNot
    } : {
        content: t('dialog.forceMuteEveryonesVideoDialog'),
        title: t('dialog.forceMuteEveryonesVideoTitle'),
        content2: t('dialog.unforceMuteEveryonesVideoDialog'),
        title1: t('dialog.unforceMuteEveryonesVideoTitle'),
        _lockAudio: fMutedorNot
    };
}
